/**
 * Store Locator Urban - Kaya Unite / Blackdrop Coffee / Adagio Teas
 * Handles search filtering, region filtering, Google Maps interactions,
 * and optional geolocation.
 */
(function () {
  'use strict';

  const SELECTORS = {
    section: '.slu',
    searchInput: '[data-slu-search]',
    filterBtn: '[data-slu-filter]',
    storeCard: '[data-slu-card]',
    storeList: '[data-slu-list]',
    listCount: '[data-slu-count]',
    mapContainer: '[data-slu-map]',
    emptyState: '[data-slu-empty]',
    locateBtn: '[data-slu-locate]',
  };

  class StoreLocatorUrban {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      this.stores = [];
      this.markers = [];
      this.map = null;
      this.infoWindow = null;
      this.activeFilter = 'all';
      this.searchQuery = '';
      this.activeCardIndex = -1;

      this._parseStores();
      this._cacheDOM();
      this._bindEvents();
      this._initMap();
    }

    /* ------------------------------------------------------------------ */
    /* Data                                                                */
    /* ------------------------------------------------------------------ */

    _parseStores() {
      const raw = this.section.querySelector('[data-slu-stores]');
      if (!raw) return;
      try {
        this.stores = JSON.parse(raw.textContent);
      } catch (e) {
        console.warn('[StoreLocatorUrban] Could not parse store data', e);
      }
    }

    /* ------------------------------------------------------------------ */
    /* DOM caching                                                         */
    /* ------------------------------------------------------------------ */

    _cacheDOM() {
      this.searchInput = this.section.querySelector(SELECTORS.searchInput);
      this.filterBtns = this.section.querySelectorAll(SELECTORS.filterBtn);
      this.storeCards = this.section.querySelectorAll(SELECTORS.storeCard);
      this.storeList = this.section.querySelector(SELECTORS.storeList);
      this.listCount = this.section.querySelector(SELECTORS.listCount);
      this.mapContainer = this.section.querySelector(SELECTORS.mapContainer);
      this.emptyState = this.section.querySelector(SELECTORS.emptyState);
      this.locateBtn = this.section.querySelector(SELECTORS.locateBtn);
    }

    /* ------------------------------------------------------------------ */
    /* Events                                                              */
    /* ------------------------------------------------------------------ */

    _bindEvents() {
      if (this.searchInput) {
        let debounce;
        this.searchInput.addEventListener('input', (e) => {
          clearTimeout(debounce);
          debounce = setTimeout(() => {
            this.searchQuery = e.target.value.trim().toLowerCase();
            this._applyFilters();
          }, 200);
        });
      }

      this.filterBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.activeFilter = btn.dataset.sluFilter;
          this.filterBtns.forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          this._applyFilters();
        });
      });

      this.storeCards.forEach((card, idx) => {
        card.addEventListener('click', () => this._selectStore(idx));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._selectStore(idx);
          }
        });
      });

      if (this.locateBtn) {
        this.locateBtn.addEventListener('click', () => this._geolocate());
      }
    }

    /* ------------------------------------------------------------------ */
    /* Filtering                                                           */
    /* ------------------------------------------------------------------ */

    _applyFilters() {
      let visibleCount = 0;

      this.storeCards.forEach((card, idx) => {
        const store = this.stores[idx];
        if (!store) return;

        const matchesRegion =
          this.activeFilter === 'all' || store.region === this.activeFilter;

        const matchesSearch =
          !this.searchQuery ||
          store.name.toLowerCase().includes(this.searchQuery) ||
          store.address.toLowerCase().includes(this.searchQuery) ||
          store.city.toLowerCase().includes(this.searchQuery);

        const visible = matchesRegion && matchesSearch;
        card.hidden = !visible;

        if (this.markers[idx]) {
          this.markers[idx].setVisible(visible);
        }

        if (visible) visibleCount++;
      });

      if (this.listCount) {
        this.listCount.textContent =
          visibleCount === 1
            ? '1 tienda encontrada'
            : visibleCount + ' tiendas encontradas';
      }

      if (this.emptyState) {
        this.emptyState.hidden = visibleCount > 0;
      }

      this._fitVisibleMarkers();
    }

    /* ------------------------------------------------------------------ */
    /* Store Selection                                                     */
    /* ------------------------------------------------------------------ */

    _selectStore(idx) {
      const store = this.stores[idx];
      if (!store || !this.map) return;

      this.storeCards.forEach((c) => c.classList.remove('is-active'));
      const card = this.storeCards[idx];
      if (card) {
        card.classList.add('is-active');
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      this.activeCardIndex = idx;

      const pos = { lat: parseFloat(store.lat), lng: parseFloat(store.lng) };
      this.map.panTo(pos);
      this.map.setZoom(16);

      if (this.infoWindow && this.markers[idx]) {
        this.infoWindow.setContent(this._buildInfoContent(store));
        this.infoWindow.open(this.map, this.markers[idx]);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Google Maps                                                         */
    /* ------------------------------------------------------------------ */

    _initMap() {
      if (!this.mapContainer) return;

      const apiKey = this.mapContainer.dataset.apiKey;
      if (!apiKey) {
        this._showMapPlaceholder('Configure tu API key de Google Maps en el Theme Editor.');
        return;
      }

      if (typeof google !== 'undefined' && google.maps) {
        this._createMap();
        return;
      }

      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        this._waitForGoogleMaps();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=__sluMapReady&loading=async`;
      script.async = true;
      script.defer = true;

      window.__sluMapReady = () => {
        document.querySelectorAll(SELECTORS.section).forEach((el) => {
          const instance = el._sluInstance;
          if (instance && !instance.map) instance._createMap();
        });
        delete window.__sluMapReady;
      };

      document.head.appendChild(script);
    }

    _waitForGoogleMaps() {
      let attempts = 0;
      const check = setInterval(() => {
        attempts++;
        if (typeof google !== 'undefined' && google.maps) {
          clearInterval(check);
          this._createMap();
        }
        if (attempts > 50) clearInterval(check);
      }, 200);
    }

    _createMap() {
      if (!this.mapContainer || this.map) return;

      const defaultCenter = { lat: -33.4489, lng: -70.6693 };
      const mapStyles = [
        { elementType: 'geometry', stylers: [{ color: '#212121' }] },
        { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
        { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
        { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
        { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
        { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
        { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
        { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
        { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
        { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
        { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
        { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
      ];

      this.map = new google.maps.Map(this.mapContainer, {
        center: defaultCenter,
        zoom: 12,
        styles: mapStyles,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
      });

      this.infoWindow = new google.maps.InfoWindow();
      this._addMarkers();
    }

    _addMarkers() {
      if (!this.map) return;

      const bounds = new google.maps.LatLngBounds();
      let hasValidCoords = false;

      this.stores.forEach((store, idx) => {
        const lat = parseFloat(store.lat);
        const lng = parseFloat(store.lng);
        if (isNaN(lat) || isNaN(lng)) {
          this.markers[idx] = null;
          return;
        }

        const position = { lat, lng };
        hasValidCoords = true;
        bounds.extend(position);

        const marker = new google.maps.Marker({
          position,
          map: this.map,
          title: store.name,
          icon: this._markerIcon(),
        });

        marker.addListener('click', () => {
          this._selectStore(idx);
        });

        this.markers[idx] = marker;
      });

      if (hasValidCoords) {
        this.map.fitBounds(bounds, { padding: 60 });

        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          if (this.map.getZoom() > 15) this.map.setZoom(15);
        });
      }
    }

    _markerIcon() {
      const accent = getComputedStyle(this.section)
        .getPropertyValue('--slu-accent')
        .trim() || '#D94F3D';

      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: accent,
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
      };
    }

    _buildInfoContent(store) {
      let html = '<div class="slu-infowindow">';
      html += `<p class="slu-infowindow__name">${this._esc(store.name)}</p>`;
      html += `<p class="slu-infowindow__address">${this._esc(store.address)}, ${this._esc(store.city)}</p>`;

      if (store.schedule) {
        html += `<div class="slu-infowindow__schedule">${store.schedule}</div>`;
      }

      if (store.maps_link) {
        html += `<a class="slu-infowindow__btn" href="${this._esc(store.maps_link)}" target="_blank" rel="noopener noreferrer">`;
        html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>';
        html += ' Cómo llegar</a>';
      }

      html += '</div>';
      return html;
    }

    _fitVisibleMarkers() {
      if (!this.map) return;
      const bounds = new google.maps.LatLngBounds();
      let count = 0;

      this.markers.forEach((marker) => {
        if (marker && marker.getVisible()) {
          bounds.extend(marker.getPosition());
          count++;
        }
      });

      if (count > 0) {
        this.map.fitBounds(bounds, { padding: 60 });
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          if (this.map.getZoom() > 15) this.map.setZoom(15);
        });
      }
    }

    _showMapPlaceholder(message) {
      if (!this.mapContainer) return;
      this.mapContainer.innerHTML = `
        <div class="slu__map-placeholder">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <p>${message}</p>
        </div>`;
    }

    /* ------------------------------------------------------------------ */
    /* Geolocation                                                         */
    /* ------------------------------------------------------------------ */

    _geolocate() {
      if (!navigator.geolocation || !this.map) return;

      this.locateBtn && this.locateBtn.setAttribute('aria-busy', 'true');

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.locateBtn && this.locateBtn.removeAttribute('aria-busy');
          const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this.map.panTo(userPos);
          this.map.setZoom(14);

          this._sortByDistance(userPos);
        },
        () => {
          this.locateBtn && this.locateBtn.removeAttribute('aria-busy');
        },
        { enableHighAccuracy: false, timeout: 8000 }
      );
    }

    _sortByDistance(userPos) {
      const indexed = this.stores.map((store, idx) => {
        const lat = parseFloat(store.lat);
        const lng = parseFloat(store.lng);
        const dist = isNaN(lat) || isNaN(lng)
          ? Infinity
          : this._haversine(userPos.lat, userPos.lng, lat, lng);
        return { idx, dist };
      });

      indexed.sort((a, b) => a.dist - b.dist);

      if (this.storeList) {
        const fragment = document.createDocumentFragment();
        indexed.forEach(({ idx }) => {
          const card = this.storeCards[idx];
          if (card && !card.hidden) fragment.appendChild(card);
        });
        indexed.forEach(({ idx }) => {
          const card = this.storeCards[idx];
          if (card && card.hidden) fragment.appendChild(card);
        });
        this.storeList.appendChild(fragment);
      }
    }

    _haversine(lat1, lng1, lat2, lng2) {
      const R = 6371;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    /* ------------------------------------------------------------------ */
    /* Utility                                                             */
    /* ------------------------------------------------------------------ */

    _esc(str) {
      if (!str) return '';
      const el = document.createElement('span');
      el.textContent = str;
      return el.innerHTML;
    }
  }

  /* ==================================================================== */
  /* Initialization                                                        */
  /* ==================================================================== */

  function init() {
    document.querySelectorAll(SELECTORS.section).forEach((el) => {
      if (el._sluInstance) return;
      el._sluInstance = new StoreLocatorUrban(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-initialize on Shopify Theme Editor section events */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector(SELECTORS.section);
      if (section) {
        section._sluInstance = null;
        section._sluInstance = new StoreLocatorUrban(section);
      }
    });
  }
})();
